import { useEffect, useRef } from 'react';

/**
 * Keep the latest value in a ref so callbacks and effects can read it without
 * taking it as a dependency.
 *
 * Use this for values that are recreated on every render but whose identity
 * carries no meaning — context callbacks built without `useCallback`, closures
 * returned by hooks like `useNavigateToChatDrawer`, or props that only need to
 * be read at call time. Depending on those directly either re-runs an effect on
 * every render or defeats the surrounding `useMemo`/`useCallback` entirely.
 *
 * The returned ref is stable, so it satisfies `exhaustive-deps` while the
 * callback still sees the current value.
 */
export const useLatestRef = <T>(value: T) => {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
};
